import React, { useEffect, useState } from "react";
import StatCard from "../components/StatCard";
import OrderCalendar from "../components/OrderCalendar";
import StatusBadge from "../components/StatusBadge";
import OrderDetailsModal from "../components/OrderDetailsModal";

export default function Dashboard() {
  const [stats, setStats] = useState([
    { title: "Total Customers", value: 0, icon: "👤", color: "bg-blue-100", textColor: "text-blue-700" },
    { title: "Total Orders", value: 0, icon: "📦", color: "bg-green-100", textColor: "text-green-700" },
    { title: "Pending Orders", value: 0, icon: "⏳", color: "bg-yellow-100", textColor: "text-yellow-700" },
    { title: "Today's Deliveries", value: 0, icon: "🚚", color: "bg-orange-100", textColor: "text-orange-700" },
  ]);
  const [deliveries, setDeliveries] = useState([]);
  const [upcomingDeliveries, setUpcomingDeliveries] = useState([]);
  const [lateDeliveries, setLateDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Calculate date range for upcoming deliveries (tomorrow to three days from now)
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const endDate = new Date();
  endDate.setDate(endDate.getDate() + 3);
  const formatDate = (date) => `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
  const upcomingRange = `${formatDate(tomorrow)} - ${formatDate(endDate)}`;

  useEffect(() => {
    setLoading(true);
    const API_URL = process.env.REACT_APP_API_URL || 
      (process.env.NODE_ENV === 'production' 
        ? 'https://teenagefashion.in/backend/api' 
        : 'http://localhost/newfashion/backend/api');
    fetch(`${API_URL}/dashboard.php`, { credentials: 'include' })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch dashboard data");
        return res.json();
      })
      .then((data) => {
        setStats([
          { title: "Total Customers", value: data.stats.total_customers, icon: "👤", color: "bg-blue-100", textColor: "text-blue-700" },
          { title: "Total Orders", value: data.stats.total_orders, icon: "📦", color: "bg-green-100", textColor: "text-green-700" },
          { title: "Pending Orders", value: data.stats.pending_orders, icon: "⏳", color: "bg-yellow-100", textColor: "text-yellow-700" },
          { title: "Today's Deliveries", value: data.stats.todays_deliveries, icon: "🚚", color: "bg-orange-100", textColor: "text-orange-700" },
          { title: "Upcoming Deliveries (Next 3 Days)", subtitle: upcomingRange, value: data.stats.upcoming_deliveries, icon: "📅", color: "bg-purple-100", textColor: "text-purple-700" },
          { title: "Completed Yet to Delivered", value: data.stats.completed_orders, icon: "✅", color: "bg-emerald-100", textColor: "text-emerald-700" },
          { title: "Late Deliveries", value: data.stats.late_deliveries, icon: "⚠️", color: "bg-red-100", textColor: "text-red-700" },
        ]);
        setDeliveries(data.deliveries || []);
        setUpcomingDeliveries(data.upcoming_deliveries_details || []);
        setLateDeliveries(data.late_deliveries_details || []);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  return (
    <div className="max-w-screen w-full mx-auto overflow-x-hidden">
      {/* Stat Cards + Chart Section */}
      <div className="w-full flex flex-col md:flex-row gap-4 mb-4 sm:mb-6">
        {/* 2x2 StatCard grid */}
        <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-2 md:grid-rows-2 gap-3 flex-1 min-w-0">
          {stats.map((s) => (
            <div key={s.title} className="w-full">
              <StatCard {...s} />
            </div>
          ))}
        </div>
        {/* Calendar on the right (on md+) */}
        <div className="w-[95vw] md:w-[380px] lg:w-[440px] flex-shrink-0 mx-auto md:mx-0">
          <OrderCalendar />
        </div>
      </div>

      {/* Buttons Section */}

      {/* Today's Deliveries Table */}
      <div className="bg-white rounded-xl shadow p-2 xs:p-3 sm:p-4 sm:p-6 mt-6 transition-all w-[93vw] md:w-[82vw] min-w-[320px] pl-3 ml-0">
        <h2 className="text-base sm:text-lg font-bold mb-2 sm:mb-4 text-gray-800">Today's Deliveries</h2>
        {loading ? (
          <div className="text-gray-500 py-4">Loading...</div>
        ) : error ? (
          <div className="text-red-500 py-4">{error}</div>
        ) : (
          <div className="overflow-x-auto w-full max-w-full">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="py-1 px-2 font-medium whitespace-nowrap">Order ID</th>
                  <th className="py-1 px-2 font-medium whitespace-nowrap">Customer Name</th>
                  <th className="py-1 px-2 font-medium whitespace-nowrap">Delivery Date</th>
                  <th className="py-1 px-2 font-medium whitespace-nowrap">Status</th>
                </tr>
              </thead>
              <tbody>
                {deliveries.length === 0 ? (
                  <tr><td colSpan="4" className="text-center text-gray-400 py-2">No deliveries for today.</td></tr>
                ) : (
                  deliveries.map((d) => (
                    <tr key={d.id} className="hover:bg-gray-50 transition">
                      <td className="py-1 px-2 text-gray-700 whitespace-normal break-words">{d.id}</td>
                      <td className="py-1 px-2 text-gray-900 font-medium whitespace-normal break-words cursor-pointer hover:underline hover:text-blue-600 transition-colors" onClick={() => { setSelectedOrder(d); setModalOpen(true); }}>
                        {d.name}
                        {d.phone && (
                          <div className="text-xs text-gray-500 font-normal mt-0.5">{d.phone}</div>
                        )}
                      </td>
                      <td className="py-1 px-2 text-gray-500 whitespace-normal break-words">{d.delivery_date}</td>
                      <td className="py-1 px-2"><StatusBadge status={d.status} /></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Upcoming Deliveries Table */}
      <div className="bg-white rounded-xl shadow p-2 xs:p-3 sm:p-4 sm:p-6 mt-6 transition-all w-[93vw] md:w-[82vw] min-w-[320px] pl-3 ml-0">
        <h2 className="text-base sm:text-lg font-bold mb-2 sm:mb-4 text-gray-800">Upcoming Deliveries</h2>
        {loading ? (
          <div className="text-gray-500 py-4">Loading...</div>
        ) : error ? (
          <div className="text-red-500 py-4">{error}</div>
        ) : (
          <div className="overflow-x-auto w-full max-w-full">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="py-1 px-2 font-medium whitespace-nowrap">Order ID</th>
                  <th className="py-1 px-2 font-medium whitespace-nowrap">Customer Name</th>
                  <th className="py-1 px-2 font-medium whitespace-nowrap">Delivery Date</th>
                  <th className="py-1 px-2 font-medium whitespace-nowrap">Status</th>
                </tr>
              </thead>
              <tbody>
                {upcomingDeliveries.length === 0 ? (
                  <tr><td colSpan="4" className="text-center text-gray-400 py-2">No upcoming deliveries.</td></tr>
                ) : (
                  upcomingDeliveries.map((d) => (
                    <tr key={d.id} className="hover:bg-gray-50 transition">
                      <td className="py-1 px-2 text-gray-700 whitespace-normal break-words">{d.id}</td>
                      <td className="py-1 px-2 text-gray-900 font-medium whitespace-normal break-words cursor-pointer" onClick={() => { setSelectedOrder(d); setModalOpen(true); }}>
                        {d.name}
                        {d.phone && (
                          <div className="text-xs text-gray-500 font-normal mt-0.5">{d.phone}</div>
                        )}
                      </td>
                      <td className="py-1 px-2 text-gray-500 whitespace-normal break-words">{d.delivery_date}</td>
                      <td className="py-1 px-2"><StatusBadge status={d.status} /></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <OrderDetailsModal open={modalOpen} onClose={() => setModalOpen(false)} order={selectedOrder} />
      {/* Late Deliveries Table */}
      <div className="bg-white rounded-xl shadow p-2 xs:p-3 sm:p-4 sm:p-6 mt-6 transition-all w-[93vw] md:w-[82vw] min-w-[320px] pl-3 ml-0">
        <h2 className="text-base sm:text-lg font-bold mb-2 sm:mb-4 text-gray-800">Late Deliveries</h2>
        {loading ? (
          <div className="text-gray-500 py-4">Loading...</div>
        ) : error ? (
          <div className="text-red-500 py-4">{error}</div>
        ) : (
          <div className="overflow-x-auto w-full max-w-full">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="py-1 px-2 font-medium whitespace-nowrap">Order ID</th>
                  <th className="py-1 px-2 font-medium whitespace-nowrap">Customer Name</th>
                  <th className="py-1 px-2 font-medium whitespace-nowrap">Delivery Date</th>
                  <th className="py-1 px-2 font-medium whitespace-nowrap">Status</th>
                </tr>
              </thead>
              <tbody>
                {lateDeliveries.length === 0 ? (
                  <tr><td colSpan="4" className="text-center text-gray-400 py-2">No late deliveries.</td></tr>
                ) : (
                  lateDeliveries.map((d) => (
                    <tr key={d.id} className="hover:bg-gray-50 transition">
                      <td className="py-1 px-2 text-gray-700 whitespace-normal break-words">{d.id}</td>
                      <td className="py-1 px-2 text-gray-900 font-medium whitespace-normal break-words cursor-pointer" onClick={() => { setSelectedOrder(d); setModalOpen(true); }}>
                        {d.name}
                        {d.phone && (
                          <div className="text-xs text-gray-500 font-normal mt-0.5">{d.phone}</div>
                        )}
                      </td>
                      <td className="py-1 px-2 text-gray-500 whitespace-normal break-words">{d.delivery_date}</td>
                      <td className="py-1 px-2"><StatusBadge status={d.status} /></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
